import React from "react";
import {
  ISendgridMessageEvent,
  SendgridMessageEventType,
  langauge,
} from "shared";
import { When } from "react-if";

import { DescriptionItem } from "../../../common/Atoms/DescriptionList";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Pill } from "../../../common/Atoms/Pill";
import { DatePopover } from "../../../common/Components/DatePopover";
import { classNames } from "../../../common/lib/classNames";
import { CustomIframe } from "../IFrame";

/** Small uppercase eyebrow used above each drawer section */
export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={ classNames(`text-[11px] font-semibold tracking-[0.09em] uppercase text-gray-400 mb-3`, className) }>
      { children }
    </div>
  );
}

/** Two column label / value grid for action details */
export function DetailGrid({ items }: { items: DescriptionItem[] }) {
  return (
    <div className={ `grid grid-cols-2 gap-x-5 gap-y-3.5` }>
      {
        items.map((item, i) => (
          <div key={ i }
            className={ `min-w-0` }>
            <div className={ `text-xs text-gray-500` }>{ item.title }</div>
            <div className={ `text-sm font-medium text-gray-900 mt-0.5 wrap-break-word whitespace-pre-line` }>
              { item.content }
            </div>
            <When condition={ !!item.subText }>
              <Paragraph variant={ `help` }>{ item.subText }</Paragraph>
            </When>
          </div>
        ))
      }
    </div>
  );
}

const dotColors: Record<string, string> = {
  [SendgridMessageEventType.Delivered]: `bg-emerald-500`,
  [SendgridMessageEventType.Processed]: `bg-emerald-500`,
  [SendgridMessageEventType.Open]: `bg-blue-500`,
  [SendgridMessageEventType.Click]: `bg-indigo-500`,
  [SendgridMessageEventType.Deferred]: `bg-amber-500`,
  [SendgridMessageEventType.Dropped]: `bg-red-500`,
  [SendgridMessageEventType.Bounce]: `bg-red-500`,
  [SendgridMessageEventType.SpamReport]: `bg-red-500`,
};

interface DeliveryTimelineProps {
  events: ISendgridMessageEvent[];
  timezone: string;
  /** Show a "retrying" pill on the latest deferred event */
  retrying?: boolean;
}

export function DeliveryTimeline({ events, timezone, retrying }: DeliveryTimelineProps) {
  const sorted = [...events].sort((a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime());

  return (
    <div className={ `flex flex-col` }>
      {
        sorted.map((e, i) => {
          const last = i === sorted.length - 1;

          return (
            <div key={ i }
              className={ `flex gap-3.5` }>
              <div className={ `flex flex-col items-center w-2.5` }>
                <span className={ classNames(`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0`, dotColors[e.eventType] || `bg-gray-400`) } />
                <When condition={ !last }>
                  <span className={ `w-px flex-1 bg-gray-200 mt-1` } />
                </When>
              </div>
              <div className={ last ? `` : `pb-4` }>
                <div className={ `text-sm font-semibold text-gray-900 flex items-center gap-1.5` }>
                  { langauge.sendgrid.event[e.eventType] || e.eventType }
                  <When condition={ last && retrying && e.eventType === SendgridMessageEventType.Deferred }>
                    <Pill size={ `xs` }
                      bordered
                      color={ `amber` }
                      message={ `retrying` } />
                  </When>
                </div>
                <div className={ `text-xs text-gray-500 mt-0.5 flex items-center gap-1` }>
                  <When condition={ !!e.timestamp }>
                    { () => (
                      <DatePopover
                        date={ e.timestamp }
                        organisationTimezone={ timezone }
                        labelFormat={ `d LLL yyyy, h:mm a` }
                      />
                    ) }
                  </When>
                  <When condition={ !!e.email }>
                    { `· ${e.email}` }
                  </When>
                </div>
              </div>
            </div>
          );
        })
      }
    </div>
  );
}

interface EmailCardProps {
  subject?: string;
  from?: string;
  replyTo?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  alsoSentTo?: string[];
  attachments?: string[];
  html?: string;
}

/** Envelope header + rendered body, matching the design's email preview card */
export function EmailCard({ subject, from, replyTo, to, cc, bcc, alsoSentTo, attachments, html }: EmailCardProps) {
  const rows = [
    { label: `Subject`, value: subject, strong: true },
    { label: `From`, value: from },
    { label: `Reply To`, value: replyTo },
    { label: `To`, value: to },
    { label: `Cc`, value: cc },
    { label: `Bcc`, value: bcc },
    { label: `Also sent to`, value: alsoSentTo?.join(`, `) },
    { label: `Attached`, value: attachments?.join(`, `) },
  ].filter(r => !!r.value);

  return (
    <div className={ `border border-gray-200 rounded-lg overflow-hidden` }>
      <When condition={ rows.length > 0 }>
        <div className={ `px-4 py-3 bg-gray-50 border-b border-gray-100 grid grid-cols-[64px_1fr] gap-x-2.5 gap-y-1 text-xs` }>
          {
            rows.map(r => (
              <React.Fragment key={ r.label }>
                <span className={ `text-gray-400` }>{ r.label }</span>
                <span className={ classNames(`wrap-break-word`, r.strong ? `text-gray-900 font-semibold` : `text-gray-700`) }>{ r.value }</span>
              </React.Fragment>
            ))
          }
        </div>
      </When>
      {
        html ? (
          <CustomIframe
            srcDoc={ html }
            style={ { width: `100%`, minHeight: `320px`, border: `none`, backgroundColor: `white` } }
          />
        ) : (
          <div className={ `p-4 text-sm text-gray-500 italic` }>{ `Archive not available` }</div>
        )
      }
    </div>
  );
}
